import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const PrivacyPage = () => {
  const { t } = useTranslation();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          {t('legal.privacy.title')}
        </PageTitle>
        <PageDate
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.1 }}
        >
          {t('legal.lastUpdated')}: April 18, 2025
        </PageDate>
      </PageHeader>

      <ContentContainer
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        transition={{ delay: 0.2 }}
      >
        <IntroSection>
          {t('legal.privacy.intro')}
        </IntroSection>
        
        <Section>
          <SectionTitle>{t('legal.privacy.sections.collection.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.collection.content')}
            <List>
              <ListItem><Strong>{t('legal.privacy.lists.personal')}</Strong></ListItem>
              <ListItem><Strong>{t('legal.privacy.lists.facial')}</Strong></ListItem>
              <ListItem><Strong>{t('legal.privacy.lists.derivative')}</Strong></ListItem>
              <ListItem><Strong>{t('legal.privacy.lists.financial')}</Strong></ListItem>
              <ListItem><Strong>{t('legal.privacy.lists.device')}</Strong></ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('legal.privacy.sections.use.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.use.content')}
            <List>
              <ListItem>{t('legal.privacy.lists.account')}</ListItem>
              <ListItem>{t('legal.privacy.lists.personalized')}</ListItem>
              <ListItem>{t('legal.privacy.lists.payments')}</ListItem>
              <ListItem>{t('legal.privacy.lists.efficiency')}</ListItem>
              <ListItem>{t('legal.privacy.lists.monitor')}</ListItem>
              <ListItem>{t('legal.privacy.lists.notify')}</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('legal.privacy.sections.disclosure.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.disclosure.content')}
            <List>
              <ListItem><Strong>{t('legal.privacy.lists.law')}</Strong></ListItem>
              <ListItem><Strong>{t('legal.privacy.lists.providers')}</Strong></ListItem>
              <ListItem><Strong>{t('legal.privacy.lists.marketing')}</Strong></ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('legal.privacy.sections.security.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.security.content')}
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('legal.privacy.sections.rights.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.rights.content')}
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('legal.privacy.sections.facial.title')}</SectionTitle>
          <SectionContent>
            <Strong>{t('legal.privacy.sections.facial.content')}</Strong>
            
            <SubSection>
              <SubSectionTitle>{t('legal.privacy.sections.facial.subsections.collect.title')}</SubSectionTitle>
              <List>
                <ListItem>{t('legal.privacy.lists.face_data_list1.item1')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list1.item2')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list1.item3')}</ListItem>
              </List>
            </SubSection>

            <SubSection>
              <SubSectionTitle>{t('legal.privacy.sections.facial.subsections.use.title')}</SubSectionTitle>
              <List>
                <ListItem>{t('legal.privacy.lists.face_data_list2.item1')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list2.item2')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list2.item3')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list2.item4')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list2.item5')}</ListItem>
              </List>
            </SubSection>

            <SubSection>
              <SubSectionTitle>{t('legal.privacy.sections.facial.subsections.sharing.title')}</SubSectionTitle>
              <List>
                <ListItem>{t('legal.privacy.lists.face_data_list3.item1')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list3.item2')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list3.item3')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list3.item4')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list3.item5')}</ListItem>
              </List>
            </SubSection>

            <SubSection>
              <SubSectionTitle>{t('legal.privacy.sections.facial.subsections.retention.title')}</SubSectionTitle>
              <List>
                <ListItem>{t('legal.privacy.lists.face_data_list4.item1')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list4.item2')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list4.item3')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list4.item4')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list4.item5')}</ListItem>
              </List>
            </SubSection>

            <SubSection>
              <SubSectionTitle>{t('legal.privacy.sections.facial.subsections.rights.title')}</SubSectionTitle>
              <List>
                <ListItem>{t('legal.privacy.lists.face_data_list5.item1')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list5.item2')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list5.item3')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list5.item4')}</ListItem>
                <ListItem>{t('legal.privacy.lists.face_data_list5.item5')}</ListItem>
              </List>
            </SubSection>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('legal.privacy.sections.children.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.children.content')}
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('legal.privacy.sections.changes.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.changes.content')}
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('legal.privacy.sections.retention.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.retention.content')}
            <List>
              <ListItem><Strong>{t('legal.privacy.lists.account_data')}</Strong></ListItem>
              <ListItem><Strong>{t('legal.privacy.lists.facial_data')}</Strong></ListItem>
              <ListItem><Strong>{t('legal.privacy.lists.usage_data')}</Strong></ListItem>
              <ListItem><Strong>{t('legal.privacy.lists.payment_data')}</Strong></ListItem>
            </List>
            {t('legal.privacy.lists.retention_note')}
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('legal.privacy.sections.international.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.international.content')}
          </SectionContent>
        </Section>

        <ContactSection>
          <SectionTitle>{t('legal.privacy.sections.contact.title')}</SectionTitle>
          <SectionContent>
            {t('legal.privacy.sections.contact.content')}
            <ContactInfo>
              <div>Xenya</div>
              <div>Email: privacy@xenya-face.app</div>
              <div>Address: 123 Beauty Lane, Paris, France</div>
            </ContactInfo>
          </SectionContent>
        </ContactSection>
      </ContentContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: ${({ theme }) => theme.colors.gradientPrimary};
`;

const PageTitle = styled(motion.h1)`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 10px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const PageDate = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.gray};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const ContentContainer = styled(motion.div)`
  max-width: 900px;
  margin: 0 auto;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  backdrop-filter: blur(10px);
  margin-top: -30px;
  margin-bottom: 60px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const IntroSection = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.7;
  margin-bottom: 40px;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.9;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const Section = styled.div`
  margin-bottom: 40px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 15px;
  color: ${({ theme }) => theme.colors.accent};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const SectionContent = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.9;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const List = styled.ul`
  padding-left: 20px;
  margin: 15px 0;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
  position: relative;
  padding-left: 5px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:before {
    content: '•';
    position: absolute;
    left: -15px;
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Strong = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const ContactSection = styled(Section)`
  margin-top: 60px;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-top: 15px;
  line-height: 1.8;
`;

const SubSection = styled.div`
  margin-top: 25px;
  margin-bottom: 25px;
  padding-left: 10px;
  border-left: 2px solid rgba(255, 255, 255, 0.1);
`;

const SubSectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.accent};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

export default PrivacyPage;